import type { Sentiment } from '../../treeSelect/types'

export interface CategoryData {
	id: number
	name: string
	parent: number | null
	sentiment: Sentiment
}

/**
 * Sentiment color mapping (matches backend Sentiment enum)
 */
const getSentimentColor = (sentiment: Sentiment): string => {
	switch (sentiment) {
		case 1:
			return '#19DA8F' // Positive - green
		case -1:
			return '#FF4955' // Negative - red
		case 0:
		case null:
		default:
			return '#A7A7A7' // Neutral - gray
	}
}

/**
 * Build mermaid node label with sentiment indicator
 * Matches backend getNodeLabel() format
 */
const buildNodeLabel = (category: CategoryData): string => {
	const color = getSentimentColor(category.sentiment)
	const name = category.name || category.id.toString()

	// Escape special characters for mermaid
	const escapedName = name.replace(/"/g, '#quot;').replace(/'/g, '#39;')

	return `<div class='flex items-center px-3 py-1 rounded-md lg:max-w-[100%] flex-shrink-0 whitespace-normal'>
                <div class='h-[14px] w-[14px] rounded-md mr-2 shrink-0' style='background-color: ${color}'></div>
                <span>${escapedName}</span>
            </div>`
}

/**
 * Build mermaid graph definition from category data
 * Matches backend buildMermaidGraph() logic
 *
 * @param categories - Array of category data with id, name, parent, sentiment
 * @returns Mermaid graph definition string
 */
export const buildMermaidDefinition = (categories: CategoryData[]): string => {
	if (categories.length === 0) {
		return 'graph LR'
	}

	const lines: string[] = ['graph LR']

	// Create a map for quick lookup
	const categoryMap = new Map<number, CategoryData>()
	categories.forEach((cat) => categoryMap.set(cat.id, cat))

	// Create nodes
	categories.forEach((cat) => {
		const nodeId = `C${cat.id}`
		const label = buildNodeLabel(cat)
		lines.push(`    ${nodeId}["${label}"]`)
	})

	// Create links (parent --> child)
	categories.forEach((cat) => {
		if (cat.parent !== null && categoryMap.has(cat.parent)) {
			const parentNodeId = `C${cat.parent}`
			const childNodeId = `C${cat.id}`
			lines.push(`    ${parentNodeId} --> ${childNodeId}`)
		}
	})

	return lines.join('\n')
}
